/**
 * Welcome Message Component - Claude Style
 *
 * Shows personalized greeting with:
 * - Quick action buttons (Revenue, Patients, Treatments, etc.)
 * - Suggested questions
 */

import * as React from 'react'
import { TrendingUp, Users, Stethoscope, BarChart3, FileSpreadsheet } from 'lucide-react'
import { cn } from '@/lib/utils'

interface WelcomeMessageProps {
  onSuggestionClick?: (question: string) => void
  userName?: string
}

// Quick actions - Claude style buttons
const quickActions = [
  {
    icon: TrendingUp,
    label: 'Revenue',
    query: 'Berapa total revenue bulan ini? Breakdown by branch.',
    color: 'text-emerald-500',
  },
  {
    icon: Users,
    label: 'Patients',
    query: 'Berapa jumlah patient baru bulan ini vs bulan lepas?',
    color: 'text-blue-500',
  },
  {
    icon: Stethoscope,
    label: 'Treatments',
    query: 'Top 10 treatments paling popular dengan revenue.',
    color: 'text-purple-500',
  },
  {
    icon: BarChart3,
    label: 'Analytics',
    query: 'Buat chart revenue trend 6 bulan terakhir.',
    color: 'text-orange-500',
  },
  {
    icon: FileSpreadsheet,
    label: 'Reports',
    query: 'Senaraikan semua invoices bulan ini.',
    color: 'text-sky-500',
  },
]

// Suggested questions
const suggestions = [
  'Listkan patient di Bangi semalam',
  'Compare revenue semua branch',
  'Which treatment has highest revenue?',
  'Patient inactive lebih 6 bulan',
]

export function WelcomeMessage({ onSuggestionClick, userName }: WelcomeMessageProps) {
  const greeting = getGreeting()
  const displayName = userName || 'there'

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh] py-6 sm:py-8 px-2 sm:px-4">
      {/* Claude-style greeting */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="inline-flex items-center justify-center mb-3 sm:mb-4">
          <span className="text-3xl sm:text-4xl">❋</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold mb-2">
          {greeting}, {displayName}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground">
          Apa yang boleh saya bantu hari ini?
        </p>
      </div>

      {/* Quick Actions - Claude style horizontal buttons */}
      <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8 max-w-2xl">
        {quickActions.map((action) => (
          <button
            key={action.label}
            onClick={() => onSuggestionClick?.(action.query)}
            className={cn(
              'flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2',
              'rounded-full border border-border bg-card',
              'hover:bg-muted transition-colors',
              'text-xs sm:text-sm font-medium'
            )}
          >
            <action.icon className={cn('h-3.5 w-3.5 sm:h-4 sm:w-4', action.color)} />
            {action.label}
          </button>
        ))}
      </div>

      {/* Suggested Questions - Hidden on mobile to reduce clutter */}
      <div className="w-full max-w-lg hidden sm:block">
        <p className="text-sm text-muted-foreground mb-3 text-center">
          Atau cuba soalan ini:
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {suggestions.map((question) => (
            <button
              key={question}
              onClick={() => onSuggestionClick?.(question)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg',
                'border border-border/50 bg-muted/30',
                'hover:bg-muted hover:border-border transition-colors',
                'text-muted-foreground hover:text-foreground'
              )}
            >
              {question}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// Get time-based greeting
function getGreeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}
